import useSWR from "swr";

import { useEffect, useMemo } from "react";

import moment from "moment";

import useAuthentication from "hooks/useAuthentication";
import { baseFetcher } from "utils/baseFetcher";

import { useTimeScope } from "./ToggleTimeScope";

export const entityFinancialURL = (slug: string) =>
  `/api/v3/entities/${slug}/financials?limit=10`;

type Number = number | null;

interface DataItem {
  asset_turnover: Number;
  assets: Number;
  cash_conversion_cycle: Number;
  cash_ratio: Number;
  change_in_deposits: Number;
  currency: string;
  current_ratio: Number;
  d2a: Number;
  deposits: Number;
  dio: Number;
  div_ps: Number;
  div_yield: Number;
  dividend_cagr_3y: Number;
  dividend_cagr_5y: Number;
  dividend_cagr_7y: Number;
  dividend_cagr_10y: Number;
  dividend_growth: Number;
  dpo: Number;
  dso: Number;
  dupont: Number;
  ebitda: Number;
  ebitda_margin: Number;
  entity_id: string;
  eps_cagr_3y: Number;
  eps_cagr_5y: Number;
  eps_cagr_7y: Number;
  eps_cagr_10y: Number;
  eps_dil: Number;
  eps_growth: Number;
  equity_multiplier: Number;
  ev_ebitda: Number;
  ev_gp: Number;
  ev_sales: Number;
  fcf_cagr_3y: Number;
  fcf_cagr_5y: Number;
  fcf_cagr_7y: Number;
  fcf_cagr_10y: Number;
  fcf_growth: Number;
  fcf_yield: Number;
  fin_cf: Number;
  free_cf_fcfe: Number;
  gross_income: Number;
  gross_margin: Number;
  income_after_llp: Number;
  inventory_turnover: Number;
  investing_cf: Number;
  liabilities: Number;
  loan_loss_prov: Number;
  loss_claim_reserve: Number;
  net_debt: Number;
  net_income: Number;
  net_margin: Number;
  netdebt_equity: Number;
  operating_cash_flow_cagr_3y: Number;
  operating_cash_flow_cagr_5y: Number;
  operating_cash_flow_cagr_7y: Number;
  operating_cash_flow_cagr_10y: Number;
  operating_cash_flow_growth: Number;
  operating_cf: Number;
  payables_turnover: Number;
  pb: Number;
  pe: Number;
  premium_receivable: Number;
  price_3y: Number;
  price_5y: Number;
  price_7y: Number;
  price_10y: Number;
  profit_cagr_3y: Number;
  profit_cagr_5y: Number;
  profit_cagr_7y: Number;
  profit_cagr_10y: Number;
  profit_growth: Number;
  quick_ratio: Number;
  receivables_turnover: Number;
  reserve_change: Number;
  revenue: Number;
  revenue_cagr_3y: Number;
  revenue_cagr_5y: Number;
  revenue_cagr_7y: Number;
  revenue_cagr_10y: Number;
  revenue_growth: Number;
  risk_provision: Number;
  roa: Number;
  roce: Number;
  rotac: Number;
  tier1_cap: Number;
  tier2_cap: Number;
  totdebt_equity: Number;
  totdebt_totassets: Number;
}

interface AnnuallyDataItem extends DataItem {
  fy_date: string;
  fy_ending: string;
}

interface QuarterlyDataItem extends DataItem {
  fq_date: string;
  fq_ending: string;
}

interface SegmentsDataItem {
  currency: string;
  entity_id: string;
  ff_segment_num: number;
  label: string;
  rev: number;
  year: string;
}

interface CurrentStats {
  currency: string;
  div_yld: Number;
  entity_id: string;
  ev_ebitda: Number;
  ev_gp: Number;
  ev_sales: Number;
  market_value_usd: Number;
  mkt_val_date: string;
  pb: Number;
  pe: Number;
}

interface SegmentsDataResult {
  date: string;
  [key: string]: any;
}

const groupSegmentsData = (data: Array<SegmentsDataItem>) => {
  const result: Array<SegmentsDataResult> = [];
  data.forEach((item) => {
    const year = new Date(item.year).getFullYear() + "";
    const existing = result.find((r) => r.date === year);
    if (!existing) {
      result.push({
        date: year,
        [item.label]: item.rev,
      });
    } else {
      existing[item.label] = item.rev;
    }
  });
  return result;
};

const sortSegmentsData = (data: Array<SegmentsDataResult>) => {
  return data.sort((d1, d2) => {
    return d1.date > d2.date ? 1 : -1;
  });
};

interface FinancialData {
  annually: Array<AnnuallyDataItem>;
  semi_annually: Array<QuarterlyDataItem>;
  quarterly: Array<QuarterlyDataItem>;
  business_segments: Array<SegmentsDataItem>;
  regional_segments: Array<SegmentsDataItem>;
  current_stats: CurrentStats;
  shareholders: Array<any>;
}

const millionizeKeys = [
  "revenue",
  "net_income",
  "gross_income",
  "ebitda",
  "assets",
  "liabilities",
  "operating_cf",
  "investing_cf",
  "fin_cf",
  "free_cf_fcfe",
  "net_debt",
];

const multiplyItem = (dataItem: DataItem) => {
  let result = {};
  millionizeKeys.forEach((key) => {
    if (key in dataItem) {
      const value = (dataItem[key as keyof DataItem] || 0) as number;
      result = {
        ...result,
        [key]: value * 1000000,
      };
    }
  });
  return {
    ...dataItem,
    ...result,
  };
};

const useFinancialData = (slug?: string) => {
  const { authenticated } = useAuthentication();
  const { data, isValidating: isLoading } = useSWR<FinancialData>(
    slug && authenticated ? entityFinancialURL(slug) : null,
    baseFetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const annually = useMemo(
    () =>
      (data?.annually || [])
        .sort((d1, d2) => {
          return d1.fy_date > d2.fy_date ? 1 : -1;
        })
        .map((item) => {
          const year = new Date(item.fy_date).getFullYear();
          return {
            date: year + "",
            original_date: item.fy_date,
            ...multiplyItem(item),
          };
        }),
    [data?.annually]
  );

  const semiAnnually = useMemo(
    () =>
      (data?.semi_annually || [])
        .sort((d1, d2) => {
          return d1.fq_date > d2.fq_date ? 1 : -1;
        })
        .map((item) => {
          let date = "";
          const d = new Date(item.fq_date);
          const semi = Math.floor((d.getMonth() + 6) / 6);
          const year = d.getFullYear();
          date = `  H${semi}\n${year}`;
          return {
            date,
            original_date: item.fq_date,
            ...multiplyItem(item),
          };
        }),
    [data?.semi_annually]
  );

  const quarterly = useMemo(
    () =>
      (data?.quarterly || [])
        .sort((d1, d2) => {
          return d1.fq_date > d2.fq_date ? 1 : -1;
        })
        .map((item) => {
          let date = "";
          const d = new Date(item.fq_date);
          const quarter = Math.floor((d.getMonth() + 3) / 3);
          const year = d.getFullYear();
          date = `  Q${quarter}\n${year}`;
          return {
            date,
            original_date: item.fq_date,
            ...multiplyItem(item),
          };
        }),
    [data?.quarterly]
  );

  const businessSegments = useMemo(
    () => sortSegmentsData(groupSegmentsData(data?.business_segments || [])),
    [data?.business_segments]
  );
  const regionalSegments = useMemo(
    () => sortSegmentsData(groupSegmentsData(data?.regional_segments || [])),
    [data?.regional_segments]
  );

  const {
    frequency,
    startDate,
    showQuarterTimeScope,
    setShowQuarterTimeScope,
    showSemiAnnualTimeScope,
    setShowSemiAnnualTimeScope,
  } = useTimeScope();

  const timeScopeData = useMemo(() => {
    const data =
      frequency === "annually"
        ? annually
        : frequency === "semi-annually"
        ? semiAnnually
        : quarterly;

    return data.filter((item) => {
      if (startDate) {
        return !moment(item.original_date).isBefore(startDate);
      }
      return true;
    });
  }, [frequency, annually, semiAnnually, quarterly, startDate]);

  useEffect(() => {
    if (data?.quarterly?.length && !showQuarterTimeScope) {
      setShowQuarterTimeScope(true);
    }
    if (data?.semi_annually?.length && !showSemiAnnualTimeScope) {
      setShowSemiAnnualTimeScope(true);
    }
  }, [
    data?.quarterly,
    data?.semi_annually,
    setShowQuarterTimeScope,
    setShowSemiAnnualTimeScope,
    showQuarterTimeScope,
    showSemiAnnualTimeScope,
  ]);

  return {
    annually,
    semiAnnually,
    quarterly,
    businessSegments,
    regionalSegments,
    timeScopeData,
    currentStats: data?.current_stats,
    shareholders: data?.shareholders,
    isLoading,
  };
};

export default useFinancialData;
